import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import Swal from "sweetalert2";
import EmailModal from "../../../Common/Emailmodale/Emailmodale";
import { MapPin } from "react-feather";

function UserManagementList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cityFilterEnabled, setCityFilterEnabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeTab, setActiveTab] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const rowsPerPage = 10;

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postData("api/users/list", {
        limit: rowsPerPage,
        page: currentPage + 1, // API uses 1-based index
        search: searchTerm,
        role: activeTab,
      });

      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.totalPages);
        setTotalUsers(response.data.totalUsers);
      }
    } catch (error) {
      console.error("Fetch users error:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, activeTab]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, activeTab, fetchUsers]); // Refetch when filters, page, or active tab changes

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page when applying filters
    fetchUsers(); // Refetch with new filters
  };

  // Toggle city filter
  const toggleCityFilter = (e) => {
    setCityFilterEnabled(e.target.checked);
    if (!e.target.checked) {
      setSearchTerm("");
    }
    setCurrentPage(0);
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setCityFilterEnabled(false);
    setActiveTab("all");
    setCurrentPage(0);
  };

  // Navigate to user details page
  const handleViewDetails = (user) => {
    navigate("/user-details", { state: user });
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0); // Reset to first page when changing tabs
  };

  // Handle user deletion
  const handleDeleteUser = async (userId, userName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete user "${userName}". This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await postData("api/users/delete-user-account", {
          userId: userId,
        });

        if (response?.success) {
          Swal.fire(
            "Deleted!",
            `User "${userName}" has been deleted successfully.`,
            "success"
          );
          fetchUsers(); // Refresh the user list
        } else {
          Swal.fire(
            "Error!",
            response?.message || "Failed to delete user",
            "error"
          );
        }
      } catch (error) {
        console.error("Delete user error:", error);
        Swal.fire("Error!", "Error deleting user", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmailClick = (user) => {
    setSelectedInquiry(user);
    setShowEmailModal(true);
  };
  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">User Management</h4>
              <p className="text-muted mb-0">Total Users: {totalUsers}</p>
            </div>
          </div>

          {/* Role Tabs */}
          <div className="row mb-3">
            <div className="col-12">
              <ul className="nav nav-tabs nav-tabs-custom">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "" ? "active" : ""}`}
                    onClick={() => handleTabChange("")}
                  >
                    All Users
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "user" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("user")}
                  >
                    Users
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === "agent" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("agent")}
                  >
                    Agents
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit} className="row g-3">
                    <div className="col-md-2">
                      <div className="form-check form-switch d-flex align-items-center h-100">
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          id="cityFilterToggle"
                          checked={cityFilterEnabled}
                          onChange={toggleCityFilter}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="cityFilterToggle"
                        >
                          City Filter
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={
                            cityFilterEnabled
                              ? "Search disabled when city filter is active"
                              : "Search by name, email or mobile..."
                          }
                          value={searchTerm}
                          onChange={(e) =>
                            !cityFilterEnabled && setSearchTerm(e.target.value)
                          }
                          readOnly={cityFilterEnabled}
                        />
                        <span className="input-group-text">
                          <i
                            className={`mdi mdi-magnify ${
                              cityFilterEnabled ? "text-muted" : ""
                            }`}
                          ></i>
                        </span>
                      </div>
                      {cityFilterEnabled && (
                        <small className="text-primary mt-1 d-block">
                          <i className="mdi mdi-information-outline me-1"></i>
                          Showing results for selected city only
                        </small>
                      )}
                    </div>
                    <div className="col-md-3 d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleReset}
                        disabled={loading}
                      >
                        Reset
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div
                    className="table-responsive"
                    style={{ overflowX: "auto" }}
                  >
                    <style>
                      {`
                        @media (max-width: 1200px) {
                          .user-table th, .user-table td {
                            white-space: nowrap;
                            min-width: 120px;
                          }
                          .user-table .actions-cell {
                            min-width: 200px;
                          }
                        }
                        .user-table th {
          
                          white-space: nowrap;
                          position: relative;
        
                        }
                        .user-table th::after {
                          content: '';
                          position: absolute;
                          right: 8px;
                          top: 50%;
                          transform: translateY(-50%);
                          width: 1px;
                          height: 20px;
                          background-color: #e9ecef;
                        }
                        .user-table th:last-child::after {
                          display: none;
                        }
                        .truncate-text {
                          max-width: 150px;
                          white-space: nowrap;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          display: inline-block;
                          vertical-align: middle;
                        }
                      `}
                    </style>
                    <table className="table table-hover table-centered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ minWidth: "180px" }}>Name</th>
                          <th style={{ minWidth: "200px" }}>Contact</th>
                          {!(activeTab === "user" || activeTab === "agent") && (
                            <th style={{ minWidth: "100px" }}>Role</th>
                          )}
                          <th style={{ minWidth: "180px" }}>Location</th>
                          <th style={{ minWidth: "120px" }}>Unique ID</th>
                          {!(activeTab === "user") && (
                            <th style={{ minWidth: "120px" }}>Referral Code</th>
                          )}
                          <th style={{ minWidth: "120px" }}>Referred By</th>
                          <th style={{ minWidth: "100px" }}>Status</th>
                          <th style={{ minWidth: "120px" }}>Joined Date</th>
                          <th
                            className="actions-cell"
                            style={{ minWidth: "200px" }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? (
                          users.map((user) => (
                            <tr key={user._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0 me-2">
                                    <div className="avatar-xs">
                                      <div className="avatar-title rounded-circle bg-light text-primary">
                                        {user.fullName
                                          ?.charAt(0)
                                          ?.toUpperCase() || "U"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex flex-column">
                                      <span
                                        className="fw-medium text-truncate"
                                        style={{ maxWidth: "120px" }}
                                        title={user.fullName}
                                      >
                                        {user.fullName}
                                      </span>
                                      {user.gender && (
                                        <small className="text-muted">
                                          {user.gender}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-column">
                                  <span
                                    className="text-truncate"
                                    style={{ maxWidth: "180px" }}
                                    title={user.email}
                                  >
                                    {user.email}
                                  </span>
                                  {user.mobile && (
                                    <small className="text-muted">
                                      {user.mobile}
                                    </small>
                                  )}
                                </div>
                              </td>
                              {!(
                                activeTab === "user" || activeTab === "agent"
                              ) && (
                                <td>
                                  <span
                                    className={`badge bg-${
                                      user.role === "user"
                                        ? "primary"
                                        : user.role === "agent"
                                        ? "success"
                                        : "danger"
                                    }`}
                                  >
                                    {user.role.charAt(0).toUpperCase() +
                                      user.role.slice(1)}
                                  </span>
                                </td>
                              )}
                              <td>
                                {user.location ? (
                                  <div className="d-flex align-items-center">
                                    <MapPin
                                      size={14}
                                      className="flex-shrink-0 me-1 text-danger"
                                    />
                                    <div className="d-flex flex-column">
                                      <span
                                        className="text-truncate"
                                        style={{ maxWidth: "150px" }}
                                        title={`${user.location.city || ""} ${
                                          user.location.state || ""
                                        }`}
                                      >
                                        {user.location.city || ""}
                                        {user.location.city &&
                                        user.location.state
                                          ? ", "
                                          : ""}
                                        {user.location.state || ""}
                                      </span>
                                      {user.location.coordinates && (
                                        <a
                                          href={`https://www.google.com/maps?q=${user.location.coordinates[1]},${user.location.coordinates[0]}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary small d-flex align-items-center"
                                          style={{ fontSize: "0.75rem" }}
                                        >
                                          <i className="mdi mdi-map-marker-radius me-1"></i>
                                          View on Map
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted small">N/A</span>
                                )}
                              </td>

                              <td>
                                <span
                                  className="text-truncate d-inline-block"
                                  style={{ maxWidth: "100px" }}
                                  title={user.uniqueId}
                                >
                                  {user.uniqueId}
                                </span>
                              </td>
                              {!(activeTab === "user") && (
                                <td>
                                  <span
                                    className="text-truncate d-inline-block"
                                    style={{ maxWidth: "100px" }}
                                    title={user.referralCode}
                                  >
                                    {user.referralCode || "N/A"}
                                  </span>
                                </td>
                              )}
                              <td
                                className="text-primary"
                                onClick={() =>
                                  user.parent_agent_id &&
                                  handleViewDetails(user.parent_agent_id)
                                }
                                style={{
                                  cursor: user.parent_agent_id
                                    ? "pointer"
                                    : "default",
                                }}
                              >
                                <span
                                  className="text-truncate d-inline-block"
                                  style={{ maxWidth: "100px" }}
                                  title={user.referredBy}
                                >
                                  {user.referredBy || "N/A"}
                                </span>
                              </td>
                              <td className="align-middle">
                                <span
                                  className={`badge bg-${
                                    user.status === "active"
                                      ? "success"
                                      : "danger"
                                  } text-uppercase`}
                                >
                                  {user.status}
                                </span>
                              </td>

                              <td className="text-nowrap">
                                <small className="text-muted">
                                  {formatDate(user.createdAt)}
                                </small>
                              </td>
                              <td className="align-middle">
                                <div className="table-actions">
                                  <button
                                    type="button"
                                    className="btn btn-icon btn-sm btn-outline-info"
                                    onClick={() => handleViewDetails(user)}
                                    title="View Details"
                                  >
                                    <i className="mdi mdi-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-icon btn-sm btn-outline-info"
                                    title="Send Email"
                                    onClick={() => handleEmailClick(user)}
                                  >
                                    <i className="mdi mdi-email-outline"></i>
                                  </button>
                                  {user.mobile && (
                                    <button
                                      className="btn btn-icon btn-sm btn-outline-success"
                                      title="Chat on WhatsApp"
                                      onClick={() => {
                                        const phoneNumber = `91${user.mobile}`;
                                        const message =
                                          "Hello, I would like to know more!";
                                        const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                                          message
                                        )}`;
                                        window.open(whatsappURL, "_blank");
                                      }}
                                    >
                                      <i className="mdi mdi-whatsapp"></i>
                                    </button>
                                  )}
                                  {user.mobile && (
                                    <a
                                      href={`tel:${user.mobile}`}
                                      className="btn btn-icon btn-sm btn-outline-primary"
                                      title="Call User"
                                    >
                                      <i className="mdi mdi-phone-outline"></i>
                                    </a>
                                  )}
                                  <button
                                    className="btn btn-icon btn-sm btn-outline-danger"
                                    title="Delete User"
                                    onClick={() =>
                                      handleDeleteUser(user._id, user.fullName)
                                    }
                                    disabled={loading}
                                  >
                                    <i className="mdi mdi-delete-outline"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              <i
                                className="fas fa-user-slash text-muted"
                                style={{ fontSize: "2rem" }}
                              ></i>
                              <p className="mt-2 mb-0">No users found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <ReactPaginate
                        previousLabel={<i className="mdi mdi-chevron-left"></i>}
                        nextLabel={<i className="mdi mdi-chevron-right"></i>}
                        breakLabel="..."
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                        pageCount={totalPages}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageChange}
                        containerClassName="pagination pagination-rounded justify-content-end mt-3"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        activeClassName="active"
                        forcePage={currentPage}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Email Modal */}
      <EmailModal
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
        selectedInquiry={selectedInquiry}
        onEmailSent={() => {
          // Handle successful email sending - could refresh data if needed
          console.log("Email sent successfully");
        }}
      />
    </div>
  );
}

export default UserManagementList;
